/**
 * `useTeamActivity` — the Team page's Activity read (#2903), over
 * `GET /api/teams/{team_id}/activity?kind=&before=&limit=`.
 *
 * Rows come ready to read from the API (verb, parts, names). Newest first, 50 per
 * page, paged by the cursor `next_before`. A 404 is final (the account is not a
 * lead of this team), so it is not retried.
 */
import { useInfiniteQuery } from "@tanstack/react-query";

import { useAuth } from "../lib/AuthContext";
import { ApiError, apiFetch } from "./client";
import type { components } from "./schema.gen";

export type TeamActivityPage = components["schemas"]["TeamActivityPage"];
export type TeamActivityRow = components["schemas"]["TeamActivityRow"];
export type TeamActivityPart = components["schemas"]["TeamActivityPart"];
export type TeamActivityKind = "access" | "cover" | "stations" | "roster";

const PAGE_SIZE = 50;

export function teamActivityQueryKey(
  teamId: string,
  kind: TeamActivityKind | null,
  accountId: string | null,
  limit: number = PAGE_SIZE,
) {
  return ["team-activity", teamId, kind ?? "all", accountId ?? "", limit] as const;
}

export interface TeamActivityOptions {
  /** Rows per page; the right panel's tail asks for a few (#2926 D9). Default 50. */
  limit?: number;
  /** False holds the read, for a viewer who has no lead view. Default true. */
  enabled?: boolean;
}

export function useTeamActivity(
  teamId: string,
  kind: TeamActivityKind | null,
  { limit = PAGE_SIZE, enabled = true }: TeamActivityOptions = {},
) {
  const { accountId } = useAuth();
  return useInfiniteQuery<TeamActivityPage, ApiError>({
    // The page size is in the key: a short page never serves the tab's 50-row pages.
    queryKey: teamActivityQueryKey(teamId, kind, accountId, limit),
    enabled: Boolean(teamId && accountId && enabled),
    staleTime: 15_000,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => {
      const qs = new URLSearchParams({ limit: String(limit) });
      if (kind) qs.set("kind", kind);
      if (typeof pageParam === "string") qs.set("before", pageParam);
      return apiFetch<TeamActivityPage>(`/api/teams/${teamId}/activity?${qs.toString()}`);
    },
    getNextPageParam: (last) => last.next_before ?? undefined,
    retry: (count, err) => {
      if (err instanceof ApiError && (err.status === 404 || err.status === 403)) return false;
      return count < 1;
    },
  });
}
